/** World events → effects: Swings, casts, warps and other moves of a fight. Moved verbatim from Effects.handle (render/effects.ts). */
import { SKILLS, getSkill } from '@nine/shared';
import { oldFxOn } from '../../oldSkillFx';
import { RAMPS, dirOf } from '../../fxSheets';
import { type FxWeapon, type SwingStyle, skillLook } from '../../skillFx';
import { viewOf } from '../../sprites';
import { realmGhosts } from '../realmStep';
import { lastShotOf } from '../../lastShot';
import { inkCast, inkCut, inkDragonFinisher, inkGenericCast, inkWarp } from '../../ink/game';
import { registerFxEvent } from './registry';

const R = Math.random;

/** The pose of each successive warp of a warp chain, in turn. */
const BLINK_POSES: readonly SwingStyle[] = ['cleave', 'rising', 'thrust', 'overhead', 'spin'];

registerFxEvent('basic', (host, ev, w) => {
  const src = w.get(ev.srcId);
  if (!src) return;
  const tgt = w.get(ev.tgtId);
  const armed = host.armed(src) || src.kind === 'monster';
  // Three blows, by weapon: a jian or dao cuts a diagonal slash, a rising backhand, then a
  // heavy overhead finisher; a spear thrusts, sweeps up, then drives home two-handed; a staff
  // sweeps, jabs, then brings it down overhead; a fan cuts, rises, then spins round; bare
  // hands push palms.
  const BASIC: Partial<Record<FxWeapon, readonly SwingStyle[]>> = {
    spear: ['thrust', 'rising', 'spear'],
    polearm: ['cleave', 'rising', 'spear'],
    staff: ['cleave', 'thrust', 'overhead'],
    fan: ['cleave', 'rising', 'fan'],
  };
  const chain = armed
    ? (BASIC[host.weaponOf(src)] ?? ['cleave', 'rising', 'overhead'])
    : ['palm', 'rising', 'palm'];
  let style: SwingStyle = chain[ev.step % 3]! as SwingStyle;
  // C0-VFX: a sword-armed rig figure's two cuts are the ink cuts, with ink trails.
  const rig = !!host.sprites.of(src)?.poses.cut;
  const inkWeapon = rig && armed && src.kind === 'player' && host.weaponOf(src) === 'jian';
  if (inkWeapon && style === 'cleave') style = 'wxCleave';
  else if (inkWeapon && style === 'rising') style = 'wxRise';
  host.startSwing(src, style, ev.step === 2);
  const finisher = ev.step === 2;
  // C0-VFX: the golden dragon palm's basic chain ends with a small dragon from the palm.
  if (finisher && src.kind === 'player' && host.world.progress.activeArt === 'golden-dragon-palm') {
    const [sx, sy] = host.pos(src);
    const [tx, ty] = tgt ? host.pos(tgt) : [sx, sy];
    inkDragonFinisher(host.fx, sx, sy, tx, ty, !!tgt, 0.3 * host.anim(src.id).swingDur);
  }
  if (style === 'wxCleave' || style === 'wxRise') {
    const [sx, sy] = host.pos(src);
    const [tx, ty] = tgt ? host.pos(tgt) : [sx, sy];
    const a = host.anim(src.id);
    inkCut(
      host.fx,
      style === 'wxRise' ? 2 : 1,
      sx,
      sy,
      tx,
      ty,
      !!tgt,
      host.iso.facing(src) < 0,
      0.14 * a.swingDur + 0.2 * a.swingDur,
    );
  } else if (host.sprites.of(src)?.poses.cut) {
    host.swingArc(src, style, finisher ? '200,240,255' : '140,220,255', finisher ? 1.35 : 1.05);
    if (ev.step === 1) host.swingArc(src, 'cleave', '255,255,255', 0.75);
  } else host.fx.push(host.bite(src));
  if (finisher && src.kind === 'player') {
    const [sx, sy] = host.pos(src);
    const h = 16 * host.tall(src);
    if (tgt && armed) {
      const [tx, ty] = host.pos(tgt);
      const y1 = ty - 14 * host.tall(tgt);
      host.fx.push({
        type: 'sheet',
        x: sx,
        y: sy - h,
        t: 0,
        life: 0.2,
        x1: tx,
        y1,
        s: { kind: 'qi', tint: 'metal', size: 1, dir: dirOf(tx - sx, y1 - sy + h) },
      });
    }
    host.fx.push({ type: 'ring', x: sx, y: sy, t: 0, life: 0.35, r: 22, col: '190,235,255' });
    host.dust(sx, sy, 8, 6, 40);
    host.impact('190,235,255', 0.06, 0.3, 3);
  }
  host.sfx.swing();
});

registerFxEvent('skill', (host, ev, _w) => {
  host.onSkill(ev.srcId, ev.skillId, ev.tgtId, ev.volley, ev.at);
});

registerFxEvent('castStart', (host, ev, w) => {
  const src = w.get(ev.srcId);
  if (src) {
    const s = getSkill(ev.skillId);
    // The pose it gathers in (skillFx.ts): a break raised high, a heal sunk in meditation, a
    // palm drawn to the waist, a spear drawn back, an ultimate coiled with rage.
    host.anim(src.id).castStyle = skillLook(s, host.weaponOf(src)).cast;
    const [x, y] = host.pos(src);
    // C0-VFX: an ink sample's wind-up is its own tell (render/ink/).
    const foe = w.get(src.cast?.targetId ?? src.targetId ?? null);
    const [fx2, fy2] = foe ? host.pos(foe) : [x, y];
    // Old skill FX off: a fighter's wind-up is its body (and the bout's tells); a monster keeps its small gather.
    if (!oldFxOn() && (src.kind === 'player' || src.brain?.defId.endsWith('boss'))) {
      host.sfx.charge();
      return;
    }
    if (inkCast(host.fx, s.id, x, y, fx2, fy2, !!foe) || inkGenericCast(host.fx, s, x, y, fx2, fy2, !!foe)) {
      host.sfx.charge();
      return;
    }
    host.fx.push({
      type: 'gather',
      x,
      y,
      t: 0,
      life: s.windup,
      r: 26 * src.scale,
      col: s.kind === 'break' ? '255,160,64' : '255,110,96',
    });
    // Gathering power: a column of light and a turning rune circle in the art's colour.
    const col =
      src.kind === 'player' ? host.elemColor(s.id) : s.kind === 'break' ? '255,160,64' : '255,90,90';
    // Only heroes and the boss get the full gathering spectacle; ordinary enemies just telegraph.
    if (src.kind === 'player' || src.brain?.defId.endsWith('boss')) {
      // In a bout the tall column would hide both fighters: only the ground sigil (a bit larger) shows the wind-up.
      const bout = !!w.pvpBout;
      if (!bout) host.fx.push({ type: 'beam', x, y, t: 0, life: s.windup + 0.15, w: 14 * src.scale, col });
      host.fx.push({ type: 'sigil', x, y, t: 0, life: s.windup + 0.2, r: (bout ? 36 : 30) * src.scale, col });
    }
  }
  host.sfx.charge();
});

registerFxEvent('whiff', (host, ev, w) => {
  const src = w.get(ev.srcId);
  if (src) host.float(src, 'พลาด', '#b9b0a0', 13);
});

registerFxEvent('death', (host, ev, w) => {
  const e = w.get(ev.id);
  if (!e) return;
  host.anim(e.id).deathAt = w.time;
  const [x, y] = host.pos(e);
  const boss = e.brain?.defId.endsWith('boss');
  host.burst(
    x,
    y - 10,
    boss ? 40 : 18,
    boss ? ['#ff4a5a', '#2a1a30', '#ffffff'] : ['#d8d0c4', '#8c8478'],
    60,
    40,
    0.7,
  );
  host.dust(x, y, 8, 8, 40);
  if (boss) host.shake = 10;
});

registerFxEvent('blink', (host, ev, w) => {
  // C0-VFX: a warp in ink — the dash stroke from where the fighter vanished, and a cut where
  // they reappear (a fading afterimage of the old effect is gone with it).
  const e = w.get(ev.id);
  const tgt = w.get(ev.tgtId);
  if (!e) return;
  const [x0, y0] = host.iso.proj(ev.fromX, ev.fromY);
  const [x1, y1] = host.pos(e);
  const [tx, ty] = tgt ? host.pos(tgt) : [x1, y1];
  const A = host.anim(e.id);
  const pose = BLINK_POSES[A.chain++ % BLINK_POSES.length]!;
  const which = pose === 'rising' ? 2 : 1;
  host.startSwing(e, which === 2 ? 'wxRise' : 'wxCleave', true);
  if (oldFxOn()) inkWarp(host.fx, getSkill(ev.skillId), x0, y0, x1, y1, tx, ty, !!tgt, which);
  host.shake = Math.max(host.shake, 3);
  host.sfx.dash();
  if (e.kind === 'player') host.cinematic(e, 1, 0.55, true);
});

registerFxEvent('spin', (host, ev, w) => {
  // Another turn of the whirlwind: a ring of blade-light round the body in the move's element
  // (a blood art's red, flinging blood out).
  const e = w.get(ev.id);
  if (!e) return;
  const [x, y] = host.pos(e);
  const sk = SKILLS[ev.skillId];
  const el = sk ? skillLook(sk, host.weaponOf(e)).element : 'blood';
  const ramp = RAMPS[el];
  const bloody = el === 'blood';
  const [body, light] = [ramp.body.join(','), ramp.light.join(',')];
  host.swingArc(e, 'spin', body, 1.5);
  host.swingArc(e, 'spin', light, 1.1);
  host.fx.push({ type: 'ring', x, y, t: 0, life: 0.35, r: 54, col: body });
  for (let k = 0; k < 3; k++) {
    const a = R() * Math.PI * 2;
    host.fx.push({
      type: 'cut',
      x: x + Math.cos(a) * 10,
      y: y - 16 + Math.sin(a) * 5,
      t: 0,
      life: 0.18,
      x1: x + Math.cos(a + 1.2) * 46,
      y1: y - 16 + Math.sin(a + 1.2) * 22,
      col: bloody ? '230,30,40' : light,
    });
  }
  if (bloody) host.bloodSpray(x, y - 16, 14, 110);
  else host.burst(x, y - 16, 10, [`rgb(${light})`, '#ffffff'], 90, 20, 0.4);
  host.shake = Math.max(host.shake, 2);
  host.sfx.swing();
});

registerFxEvent('skillDash', (host, ev, w) => {
  // Afterimages along the path the move carried the fighter.
  const e = w.get(ev.id);
  if (!e) return;
  const set = host.sprites.of(e);
  if (!set) return;
  const { set: view, facing } = viewOf(set, host.iso.heading(e), host.iso.facing(e));
  const [x1, y1] = host.pos(e);
  const [x0, y0] = host.iso.proj(ev.fromX, ev.fromY);
  host.dashFrom.set(e.id, [x0, y0]);
  // The pose it is in as the move starts (render/lastShot.ts), else the dash still.
  const cur = lastShotOf(e.id, w.time);
  const nGhost = 3 + (e === w.player ? realmGhosts(w) : 0);
  for (let k = 0; k < nGhost; k++) {
    const f = k / nGhost;
    host.fx.push({
      type: 'ghost',
      step: true,
      x: x0 + (x1 - x0) * f,
      y: y0 + (y1 - y0) * f,
      t: -0.02 * k,
      life: 0.4,
      img: cur?.img ?? view.poses.dash ?? view.walk[1]!,
      flip: (cur ? cur.facing : facing) < 0,
      ax: view.ax,
      ay: view.ay,
    });
    host.snowBurst(x0 + (x1 - x0) * f, y0 + (y1 - y0) * f - 4, 3, false);
  }
});

registerFxEvent('status', (host, ev, w) => {
  const e = w.get(ev.id);
  if (!e || ev.stacks <= 0) return;
  const [x, y] = host.pos(e);
  const cy = y - 14 * host.tall(e) - e.z;
  if (ev.status === 'snow-daze') host.snowBurst(x, cy, 4 + ev.stacks * 3, true);
  else if (ev.status === 'chill' || ev.status === 'frost-trap')
    host.fx.push({ type: 'ring', x, y, t: 0, life: 0.6, r: 18, col: '160,220,255' });
});

registerFxEvent('teleBurst', (host, ev, _w) => {
  const [x, y] = host.iso.proj(ev.x, ev.y);
  host.fx.push({ type: 'ring', x, y, t: 0, life: 0.45, r: ev.radius * 24, col: '255,70,80' });
  host.fx.push({ type: 'star', x, y: y - 6, t: 0, life: 0.25, r: 22, col: '255,120,120' });
  host.burst(x, y, 26, ['#ff4a5a', '#3a1020', '#ffb0b0'], 90, 30, 0.6);
  host.dust(x, y, 14, ev.radius * 14, 60);
  host.shake = Math.max(host.shake, 6);
  host.sfx.boom();
});
